<template>
  <!-- eslint-disable prefer-standard-components/no-vuetify-inputs -->
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
    :sm="sm"
  >
    <v-row dense>
      <v-col
        v-if="label"
        :class="labelClass"
        cols="12"
      >
        {{ label }}
        <RequiredIndicator v-if="mandatory" />
        <template v-if="helpDocument && openHelpFlyout">
          <HelpIcon
            :document="helpDocument"
            :on-help-click="openHelpFlyout"
            icon-class="mb-1 cursor-pointer d-inline ps-1"
            location="top"
            size="x-small"
          />
        </template>
        <v-tooltip
          v-else-if="tooltipText"
          :open-on-click="$vuetify.display.smAndDown"
          :text="$bct(tooltipText)"
          location="top"
        >
          <template #activator="{ props: tooltipProps }">
            <v-icon
              v-bind="tooltipProps"
              class="mb-1"
              icon="info_outline"
              size="x-small"
            />
          </template>
        </v-tooltip>
      </v-col>

      <v-col
        v-if="description"
        class="fs-16 c-light-black my-1"
        cols="12"
      >
        <span>
          {{ $bct(description) }}
        </span>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-text-field
          v-model="localValue"
          @keydown="handleNumberKeydown"
          @paste="handleNumberPaste"
          @update:model-value="handleUpdateModelValue"
          :append-inner-icon="appendIcon"
          :aria-label="ariaLabel"
          :autofocus="autofocus"
          :class="inputClass"
          :density="dense ? 'compact' : undefined"
          :disabled="locked || undefined"
          :label="$bct(title || '')"
          :max="max"
          :message="$bct(message || '')"
          :min="min"
          :readonly="readonly || undefined"
          :step="stepAttribute"
          data-cy="labeled-number-field"
          data-testid="labeled-number-field"
          type="number"
          variant="filled"
          hide-details
          round
          tile
        />
      </v-col>
    </v-row>

    <v-divider
      v-if="dividedBottom"
      class="mt-4 mb-2"
    />
  </v-col>
</template>

<script setup>
import HelpIcon from '@/shared/components/form/HelpIcon.vue';
import propsToRefs from '@/shared/utils/propsToRefs';
import RequiredIndicator from '@/shared/components/RequiredIndicator.vue';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';

const attrs = useAttrs();
const emit = defineEmits(useLabeledField.emits);
defineExpose({ attrs, emit });

const localValue = defineModel({ type: undefined });

const props = defineProps({
  dividedBottom: {
    type: Boolean,
    default: false,
  },
  integerOnly: {
    type: Boolean,
    default: false,
  },
  max: {
    type: Number,
    default: null,
  },
  min: {
    type: Number,
    default: 0,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  step: {
    type: [Number, String],
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
  tooltipText: {
    type: String,
    default: null,
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

const { appendIcon, inputClass, labelClass, handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);

const { enabled, ariaLabel, helpDocument, label, locked, openHelpFlyout } = useSchematizedField(
  ...propsToRefs(props, useSchematizedField.paramKeys),
);

const $bct = inject('translator');

const stepAttribute = computed(() => {
  if (props.step !== null && props.step !== undefined && props.step !== '') return props.step;
  if (props.integerOnly) return 1;
  return undefined;
});

const integerOnlyNavigationKeys = new Set([
  'Backspace',
  'Tab',
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Delete',
  'Home',
  'End',
]);

function handleNumberKeydown(event) {
  if (!props.integerOnly) return;
  if (integerOnlyNavigationKeys.has(event.key)) return;
  if (event.ctrlKey || event.metaKey) return;
  if (/^\d$/.test(event.key)) return;
  event.preventDefault();
}

function handleNumberPaste(event) {
  if (!props.integerOnly) return;
  const clipboardText = event.clipboardData?.getData('text') ?? '';
  if (!/^\d*$/.test(clipboardText)) event.preventDefault();
}
</script>
